import type { PlanningNature, TransferPurpose } from '@pfo/domain';

import type { ExpenseDraft, TransferDraft } from '../../transactions/data/quickAddRepository.js';

/**
 * A bank notice waiting in /cho-duyet (decision AL1): staged by a machine from the bank's
 * confirmation email, not on the books until a person approves it.
 */
export type NamedRef = Readonly<{ id: string; name: string }>;

/** Something already in the ledger that looks like the same payment (same account, amount, ±1 day). */
export type SimilarLine = Readonly<{
  id: string;
  amount: number;
  occurredAt: string;
  description: string | null;
  source: string;
}>;

export type PendingKind = 'expense' | 'transfer';

export type PendingEntry = Readonly<{
  id: string;
  /** Instant the bank reported, already in UTC. */
  occurredAt: string;
  amount: number;
  kind: PendingKind;
  counterparty: string | null;
  note: string | null;
  bank: string | null;
  account: NamedRef;
  toAccount: NamedRef | null;
  suggestedCategory: NamedRef | null;
  category: NamedRef | null;
  planningNature: PlanningNature | null;
  purpose: TransferPurpose | null;
  forFamily: boolean;
  similar: readonly SimilarLine[];
}>;

/** `count` and `total` cover every pending row and are summed in SQL (DBD-014). */
export type PendingInbox = Readonly<{
  count: number;
  total: number;
  items: readonly PendingEntry[];
}>;

/** What a person may change before approving. Present keys win (null clears); absent keys keep. */
export type PendingPatch = Readonly<{
  kind?: PendingKind;
  amount?: number;
  occurredAt?: string;
  note?: string | null;
  accountId?: string;
  toAccountId?: string | null;
  categoryId?: string | null;
  planningNature?: PlanningNature | null;
  purpose?: TransferPurpose | null;
  forFamily?: boolean;
}>;

/** The category a one-tap approval posts with: the person's own choice, else the suggestion. */
export function effectiveCategory(entry: PendingEntry): NamedRef | null {
  return entry.category ?? entry.suggestedCategory;
}

/**
 * One tap is enough only when nothing is missing. An expense without any category, or a
 * transfer without a destination, has to go through "Sửa" — the database would refuse it.
 */
export function canQuickApprove(entry: PendingEntry): boolean {
  return entry.kind === 'expense' ? effectiveCategory(entry) !== null : entry.toAccount !== null;
}

/** The patch for a one-tap approval: exactly what the row shows. */
export function quickApprovePatch(entry: PendingEntry): PendingPatch {
  const category = effectiveCategory(entry);
  return entry.kind === 'expense' && category !== null ? { categoryId: category.id } : {};
}

type DraftMeta = Readonly<{ date: string; initialDate: string }>;

/**
 * An untouched day keeps the exact time the bank reported. The form turns a day into
 * "now" (today) or midday (another day), which would move 19:30 to 12:00.
 */
function occurredAtPatch(occurredAt: string, meta: DraftMeta): PendingPatch {
  return meta.date === meta.initialDate ? {} : { occurredAt };
}

/** The "Sửa" form of an expense, turned into the patch the RPC takes — every field the form owns. */
export function patchFromExpenseDraft(draft: ExpenseDraft, meta: DraftMeta): PendingPatch {
  return {
    kind: 'expense',
    amount: draft.amount,
    accountId: draft.accountId,
    categoryId: draft.categoryId,
    // The form owns the note: an emptied field clears it rather than keeping the old one.
    note: draft.note ?? null,
    forFamily: draft.forFamily === true,
    ...(draft.planningNature === undefined ? {} : { planningNature: draft.planningNature }),
    ...occurredAtPatch(draft.occurredAt, meta),
  };
}

export function patchFromTransferDraft(draft: TransferDraft, meta: DraftMeta): PendingPatch {
  return {
    kind: 'transfer',
    amount: draft.amount,
    accountId: draft.fromAccountId,
    toAccountId: draft.toAccountId,
    note: draft.note ?? null,
    purpose: draft.purpose ?? null,
    ...occurredAtPatch(draft.occurredAt, meta),
  };
}

/** The title of a row: who was paid, or what the bank called it when the notice has no name. */
export function titleOf(entry: PendingEntry): string {
  return entry.counterparty ?? (entry.kind === 'transfer' ? 'Chuyển tiền' : 'Chuyển khoản');
}
